import { SearchSuggestion, CacheEntry } from '../types';

export class SearchService {
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly CACHE_PREFIX = 'subreddit_search_';

  private getCacheKey(query: string): string {
    return `${this.CACHE_PREFIX}${query.toLowerCase()}`;
  }

  private getCachedResults(query: string): SearchSuggestion[] | null {
    try {
      const cacheKey = this.getCacheKey(query);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const { data, timestamp }: CacheEntry<SearchSuggestion[]> = JSON.parse(cached);
      const now = Date.now();

      if (now - timestamp > this.CACHE_DURATION) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  }

  private setCachedResults(query: string, data: SearchSuggestion[]) {
    try {
      const cacheKey = this.getCacheKey(query);
      const cacheData: CacheEntry<SearchSuggestion[]> = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error writing to cache:', error);
      this.cleanupOldCache(); // Try to free up space
    }
  }

  private cleanupOldCache() {
    const now = Date.now();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.CACHE_PREFIX)) {
        try {
          const cached: CacheEntry<any> = JSON.parse(localStorage.getItem(key) || '');
          if (now - cached.timestamp > this.CACHE_DURATION) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          localStorage.removeItem(key); // Remove invalid cache entries
        }
      }
    }
  }

  async searchSubreddits(query: string): Promise<SearchSuggestion[]> {
    if (query.length < 2) return [];

    // Check cache first
    const cachedResults = this.getCachedResults(query);
    if (cachedResults) {
      return cachedResults;
    }

    try {
      const response = await fetch(
        `https://www.reddit.com/api/subreddit_autocomplete_v2.json?query=${encodeURIComponent(query)}&include_over_18=true&limit=10`
      );
      const data = await response.json();
      const suggestions = data.data.children;

      // Cache the results
      this.setCachedResults(query, suggestions);

      return suggestions;
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      return [];
    }
  }

  // Initialize and cleanup
  init() {
    this.cleanupOldCache();
  }
}
